import styled, { keyframes } from "styled-components";

import { TextArea } from "../Input/textArea";

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/* Field + error line; the label stays the next sibling, as FormGroup expects */
export const Container = styled.div<{ $loading: boolean }>`
  margin: 0.5rem 0;

  /* The label is the next sibling of this wrapper now, not of the textarea */
  &:focus-within + label {
    color: var(--color-neutral-white);
  }

  /* The textarea and, inside it at the bottom right, the AI button */
  .field {
    position: relative;
  }

  ${TextArea} {
    display: block;
    /* Room for the button row so it never covers the last line */
    padding-bottom: 2.75rem;
    margin: 0;
  }

  /* While the AI writes: a light moving sheen over the field */
  .field::after {
    position: absolute;
    inset: 0;
    pointer-events: none;
    content: "";
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
    background-size: 200% 100%;
    border-radius: 0.625rem;
    opacity: ${props => (props.$loading ? 1 : 0)};
    transition: opacity 0.2s;
    animation: ${shimmer} 1.4s linear infinite;
  }
`;

export const Actions = styled.div`
  position: absolute;
  right: 12px;
  bottom: 15px;
  z-index: 1;
  display: flex;
  gap: 0.625rem;
  align-items: center;

  .undo,
  .cancel {
    font-family: Inter, sans-serif;
    font-size: 0.6875rem;
    font-weight: 700;
    color: var(--color-neutral-150);

    &:hover {
      color: var(--color-neutral-white);
    }
  }
`;

/* Discreet gray (like the cards' action buttons); only the sparkle is colored */
export const AiButton = styled.button`
  display: inline-flex;
  gap: 0.3125rem;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: var(--color-neutral-white);
  background: var(--color-neutral-base);
  border-radius: 100px;
  transition:
    background-color 0.15s,
    opacity 0.15s;

  &:hover:not(:disabled) {
    background: var(--color-neutral-light);
  }

  /* The AI sparkle keeps a touch of color */
  > svg {
    color: #ffd00d;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.75;
  }

  .spinner {
    width: 10px;
    height: 10px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 100%;
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const ErrorText = styled.p`
  margin-top: 0.375rem;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-feedback-danger);
`;
